import {
  Dropdown,
  DropdownItem,
  InputField,
  MultiSelectFilterDropdown,
} from 'plume-ui';
import i18n from '../../../../../utils/i18n';
import { verifyNumberInput } from '../helpers';
import { FilterDataType, FilterPatternType } from '../types';
import MultiStringSelectInput from './inputs/MultiStringSelectInput';
import { FilterInputComponentMap } from './types';

const t = i18n.t.bind(i18n);

export const locationFilterInputMap: FilterInputComponentMap = {
  [FilterPatternType.String]: {
    withAllowedValues: {
      render({ onChange, filter }) {
        const label = filter.columnValue || filter.allowedValues[0];
        return (
          <Dropdown label={label} closeOnItemClick>
            {filter.allowedValues?.map((value) => (
              <DropdownItem
                key={value}
                onClick={() => {
                  onChange?.(value);
                }}
              >
                {value}
              </DropdownItem>
            ))}
          </Dropdown>
        );
      },
    },
    withoutAllowedValues: {
      render({ onChange, filter }) {
        return (
          <MultiStringSelectInput
            limitSelection={1}
            columnName={filter.columnName}
            values={filter.columnValue ? [filter.columnValue] : []}
            onChange={(items) => {
              onChange?.(items[0]);
            }}
            title={t('monitorAdd.selectValuesFor', {
              attribute: filter.attribute,
              interpolation: { escapeValue: false },
            })}
          />
        );
      },
    },
  },
  [FilterPatternType.Numeric]: {
    withoutAllowedValues: {
      render({ onChange, filter }) {
        const isFloat = filter.dataType === FilterDataType.Float;
        return (
          <InputField
            value={filter.columnValue}
            min={0}
            step={isFloat ? 0.01 : 1}
            type="number"
            onInput={(e) => {
              const val = (e.target as HTMLInputElement).value;
              const inputValue = verifyNumberInput(val, Infinity, 0);
              if (!isNaN(inputValue)) onChange?.(inputValue);
            }}
          />
        );
      },
    },
  },
  [FilterPatternType.Time]: {
    withoutAllowedValues: {
      render({ onChange, filter }) {
        return (
          <InputField
            value={filter.columnValue.toString()}
            min={0}
            type="number"
            label={i18n.t('genericLabels.days')}
            onInput={(e) => {
              const val = (e.target as HTMLInputElement).value;
              if (val) onChange?.(parseInt(val));
            }}
          />
        );
      },
    },
  },
  [FilterDataType.Bool]: {
    withAllowedValues: {
      render({ onChange, filter }) {
        const label = {
          true: t('monitorAdd.booleans.true'),
          false: t('monitorAdd.booleans.false'),
          undefined:
            filter.allowedValues[0].toLowerCase() === 'true'
              ? t('monitorAdd.booleans.true')
              : t('monitorAdd.booleans.false'),
        }[String(filter.columnValue)];

        return (
          <Dropdown label={label} closeOnItemClick>
            {filter.allowedValues?.map((value) => (
              <DropdownItem
                selected={String(filter.columnValue) === value.toLowerCase()} // values are "True" and "False"
                key={value}
                onClick={() => {
                  onChange?.(value.toLowerCase() === 'true');
                }}
              >
                {value}
              </DropdownItem>
            ))}
          </Dropdown>
        );
      },
    },
    withoutAllowedValues: {
      render({ onChange, filter }) {
        const label = {
          true: t('monitorAdd.booleans.true'),
          false: t('monitorAdd.booleans.false'),
          undefined: t('genericLabels.select'),
        }[String(filter.columnValue)];

        return (
          <Dropdown label={label} closeOnItemClick>
            <DropdownItem
              selected={filter.columnValue === true}
              onClick={() => {
                onChange?.(true);
              }}
            >
              {t('monitorAdd.booleans.true')}
            </DropdownItem>
            <DropdownItem
              selected={filter.columnValue === false}
              onClick={() => {
                onChange?.(false);
              }}
            >
              {t('monitorAdd.booleans.false')}
            </DropdownItem>
          </Dropdown>
        );
      },
    },
  },
  [FilterDataType.StringArray]: {
    withoutAllowedValues: {
      render({ onChange, filter }) {
        const limitSelection = filter.operator === 'in' ? undefined : 1;
        const values = ((filter.columnValue || []) as string[]).slice(
          0,
          limitSelection,
        );
        return (
          <MultiStringSelectInput
            limitSelection={limitSelection}
            columnName={filter.columnName}
            values={values}
            onChange={onChange}
            title={t('monitorAdd.selectValuesFor', {
              attribute: filter.attribute,
              interpolation: { escapeValue: false },
            })}
          />
        );
      },
    },
    withAllowedValues: {
      render({ onChange, filter }) {
        return (
          <MultiSelectFilterDropdown
            items={filter.allowedValues?.map((value) => ({
              label: value,
              value: value,
            }))}
            selectedItems={(filter.columnValue ?? []).map((value: string) => ({
              label: value,
              value: value,
            }))}
            onSelect={(value) => {
              onChange?.(value.map((item) => item.value));
            }}
            containerLabel={''}
            defaultLabel={t('genericLabels.selectItems')}
            multiItemsSelectedLabel={
              filter.columnValue.at(0) || t('genericLabels.select')
            }
          />
        );
      },
    },
  },
};
